// The link we email for choosing a Dashboard password carries its token in the
// address fragment, which browsers never send to a server, so no request log
// ever holds it. But any script on the page can read the address: Sentry
// records it with errors and in its navigation history, and the Dashboard's
// trackers send it with page views and session recordings. Anyone who saw an
// unused token there could choose that account's password. So the token is
// taken out of the address before any of them starts, and the page reads it
// from here instead.
export const SET_PASSWORD_PATH = "/manage/set-password";
const TOKEN_PARAM = "token";
// Per tab, so a reload — or a phone restoring the tab — still has the link,
// while nothing that reads the address or the page does.
const TOKEN_STORAGE_KEY = "fm.passwordLinkToken";

let token = "";

function onSetPasswordPage(): boolean {
  return window.location.pathname.replace(/\/+$/, "") === SET_PASSWORD_PATH;
}

function tokenInAddress(): string {
  if (!onSetPasswordPage()) {
    return "";
  }

  return (
    new URLSearchParams(window.location.hash.slice(1)).get(TOKEN_PARAM) ?? ""
  );
}

function keepForTab(value: string): void {
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, value);
  } catch {
    // Storage can be off; the tab still holds the token until it reloads.
  }
}

function clearAddressFragment(): void {
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname + window.location.search
  );
}

/**
 * Moves a link's token from the address into this module. Must run before
 * anything that reads the address starts, or that thing sees the token.
 * Answers whether there was one to take.
 */
export function takePasswordLinkToken(): boolean {
  const fromAddress = tokenInAddress();

  if (!fromAddress) {
    return false;
  }

  token = fromAddress;
  keepForTab(fromAddress);
  clearAddressFragment();
  return true;
}

/**
 * Takes a token the app starts with, and one that arrives later in the same
 * tab. A second link opened on this page changes only the fragment, so the app
 * never restarts to take it; it is taken as the address changes — before the
 * router, Sentry or a tracker hears of the change, since they listen later —
 * and the page reloads to start over with it.
 */
export function guardPasswordLinkToken(
  reload: () => void = () => window.location.reload()
): void {
  takePasswordLinkToken();

  const takeArrivingToken = () => {
    if (takePasswordLinkToken()) {
      reload();
    }
  };

  // Browsers announce a fragment change with both; whichever comes first takes
  // the token, and the other then finds nothing left.
  window.addEventListener("popstate", takeArrivingToken);
  window.addEventListener("hashchange", takeArrivingToken);
}

/** The token of the link this tab opened, or "" when there is none. */
export function passwordLinkToken(): string {
  if (token) {
    return token;
  }

  try {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

/** Once the link is spent or dead, nothing about it is kept. */
export function forgetPasswordLinkToken(): void {
  token = "";

  try {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Nothing was stored.
  }
}
